import { motion } from "framer-motion";
import { Search, Stethoscope, PenTool, Hammer, Rocket, GraduationCap, TrendingUp } from "lucide-react";

const STAGES = [
  { Icon: Search, label: "Discover", copy: "We learn how your business works." },
  { Icon: Stethoscope, label: "Diagnose", copy: "We identify what needs to be measured." },
  { Icon: PenTool, label: "Design", copy: "We create a solution tailored to your business." },
  { Icon: Hammer, label: "Develop", copy: "We build your dashboards and reports." },
  { Icon: Rocket, label: "Deploy", copy: "We implement and integrate the solution." },
  { Icon: GraduationCap, label: "Develop Capability", copy: "We train your team to use it confidently." },
  { Icon: TrendingUp, label: "Drive Improvement", copy: "We support continuous improvement as your business grows." },
];

export default function OpsMethodology() {
  return (
    <section id="methodology" data-testid="ops-methodology" className="relative bg-[#142645] text-[#F1ECE0] border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 ops-grid-bg opacity-30 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-14 md:mb-16 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#D9FF3C] mb-6 inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D9FF3C]" />
            Our methodology
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            The OpsIQ <span className="italic">7D</span> Methodology<span className="text-[#D9FF3C]">™</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#F1ECE0]/70 max-w-2xl">
            A proven seven-stage path — from first workshop to steady-state improvement.
            Every OpsIQ engagement follows the same disciplined process.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-white/15" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 2.2, ease: "easeInOut" }}
            style={{ transformOrigin: "0% 50%" }}
            className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-[#D9FF3C]"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6 lg:gap-4">
            {STAGES.map(({ Icon, label, copy }, i) => (
              <motion.div
                key={label}
                data-testid={`stage-${String(i + 1).padStart(2, "0")}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                whileHover={{ y: -4 }}
                className="relative lg:pt-16"
              >
                <span className="hidden lg:flex absolute top-4 left-0 h-8 w-8 rounded-full border border-[#D9FF3C] bg-[#142645] items-center justify-center">
                  <Icon size={14} strokeWidth={1.6} className="text-[#D9FF3C]" />
                </span>
                <div className="lg:hidden mb-4 h-12 w-12 rounded-full border border-[#D9FF3C]/60 flex items-center justify-center">
                  <Icon size={18} strokeWidth={1.5} className="text-[#D9FF3C]" />
                </div>
                <div className="font-plex-mono text-[10px] uppercase tracking-[0.22em] text-[#F1ECE0]/45 mb-2">
                  Stage · {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-editorial text-xl md:text-2xl leading-tight tracking-tight">{label}</div>
                <p className="mt-2 text-xs md:text-sm text-[#F1ECE0]/65 leading-relaxed">{copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
